import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  Heading,
  Icon,
  Spacer,
  Spinner,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  LinkIcon,
  LockClosedIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Search from '@/components/list-controls/search';
import { SimpleSelect } from '@/components/ui/simple-select';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Avatar } from '@/components/ui/avatar';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { buildPublicUrl } from '@/utils/public-url';
import { slotFor } from '@/lib/slot-color';
import useCoursesStore from '@/utils/use-courses';
import useCurrentUser from '@/lib/hooks/use-current-user';
import useWorkspace from '@/lib/hooks/use-workspace';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import { Course, CourseStatus } from '@/lib/types';

type HubTab = 'workspace' | 'community' | 'mine' | 'assigned';
type StatusFilter = 'all' | CourseStatus;

const ALL = 'all';

const STATUS_ITEMS = [
  { label: 'All statuses', value: ALL },
  { label: 'Published', value: 'published' },
  { label: 'Draft', value: 'draft' },
  { label: 'Archived', value: 'archived' },
];

const ACCESS_ITEMS = [
  { label: 'All access', value: ALL },
  { label: 'Open link', value: 'open' },
  { label: 'Log-in only', value: 'login' },
];

// Distinct values of one course field, shaped for SimpleSelect behind an
// "All …" first entry.
function optionsFrom(courses: Course[], pick: (c: Course) => string | undefined, allLabel: string) {
  const values = new Set<string>();
  courses.forEach((course) => {
    const value = pick(course);
    if (value) values.add(value);
  });
  return [
    { label: allLabel, value: ALL },
    ...[...values].sort().map((value) => ({ label: value, value })),
  ];
}

// Parses a 'YYYY-MM-DD' wire date as a local calendar date — new Date(str)
// would land it a day early west of Greenwich.
function parseWireDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// The due chip on an assigned course card: neutral until the due date is
// near, danger once it's within a week or past.
function dueChip(course: Course): { label: string; urgent: boolean } | null {
  if (!course.due_date) return null;
  const due = parseWireDate(course.due_date);
  const msLeft = due.getTime() + 24 * 60 * 60 * 1000 - Date.now();
  if (msLeft < 0) return { label: `Was due ${formatShortDate(due)}`, urgent: true };
  if (msLeft < 7 * 24 * 60 * 60 * 1000) return { label: `Due ${formatShortDate(due)}`, urgent: true };
  return { label: `Due ${formatShortDate(due)}`, urgent: false };
}

// Status pill colors: published = success tint, draft = neutral, archived =
// muted. Uses semantic tokens only — they forward to the DS variables.
function statusColors(status: CourseStatus): { bg: string; color: string } {
  if (status === 'published') return { bg: 'success.soft', color: 'success' };
  if (status === 'draft') return { bg: 'bg.subtle', color: 'fg.muted' };
  return { bg: 'bg.subtle', color: 'fg.subtle' };
}

// CompletionCell is the hub's completion column: a done-of-assigned bar for
// individually tracked courses, anonymous counters for count-only ones.
function CompletionCell({ course }: { course: Course }) {
  if (course.status === 'draft' || !course.shared_at) {
    return (
      <Box>
        <Text fontSize='sm' fontWeight='600' color='fg.strong'>
          Not shared yet
        </Text>
        <Text fontSize='xs' color='fg.muted'>
          No completion data
        </Text>
      </Box>
    );
  }

  if (course.tracking_mode === 'individual') {
    if (course.assigned_count === 0) {
      return (
        <Text fontSize='sm' color='fg.muted'>
          Nobody assigned yet
        </Text>
      );
    }
    const pct = (course.completed_learner_count / course.assigned_count) * 100;
    return (
      <Box maxW='190px'>
        <ProgressBar
          value={pct}
          // The prototype turns healthy completion green; in-progress stays brand.
          fill={pct >= 75 ? 'success' : undefined}
          label={`${course.completed_learner_count} of ${course.assigned_count} complete`}
          valueLabel={`${Math.round(pct)}%`}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize='sm' fontWeight='600' color='fg.strong'>
        {course.completed_learner_count} completed
      </Text>
      <Text fontSize='xs' color='fg.muted'>
        {course.started_count} started · untracked
      </Text>
    </Box>
  );
}

// "Assigned to me": active courses as cards under the season-pace banner,
// finished ones in a compact transcript below — mirroring the prototype's
// learner hub.
function AssignedTab({ assigned, workspaceId }: { assigned: Course[]; workspaceId: string }) {
  const navigate = useNavigate();
  const isComplete = (course: Course) =>
    course.item_count > 0 && course.completed_item_count >= course.item_count;
  const active = assigned.filter((course) => !isComplete(course));
  const completed = assigned.filter(isComplete);
  // One banner: the first unfinished course with an active season pace.
  const paced = active.find((course) => course.pace);

  return (
    <>
      {paced?.pace && (
        <Flex
          align='center'
          gap={3}
          bg='brand.soft'
          borderWidth='1px'
          borderColor='brand.emphasized'
          borderRadius='lg'
          px={4}
          py={3}
          mb={5}
          wrap='wrap'
        >
          <Icon as={CalendarDaysIcon} w={5} h={5} color='brand.fg' />
          <Text fontSize='sm' color='fg'>
            Week {paced.pace.position} of the season — you should be on{' '}
            <Text as='span' fontWeight='700'>
              {paced.pace.module_name || `module ${paced.pace.position}`}
            </Text>{' '}
            in {paced.name}.
          </Text>
          {paced.pace.last_reminder_at && (
            <Text fontSize='xs' color='fg.muted' ml='auto'>
              Reminder sent{' '}
              {new Date(paced.pace.last_reminder_at).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </Text>
          )}
        </Flex>
      )}

      {active.length > 0 && (
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
          {active.map((course) => {
            const pct =
              course.item_count > 0
                ? (course.completed_item_count / course.item_count) * 100
                : 0;
            const due = dueChip(course);
            return (
              <Box
                key={course.id}
                bg='bg.panel'
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                shadow='sm'
                overflow='hidden'
                cursor='pointer'
                _hover={{ shadow: 'md' }}
                onClick={() => navigate(`/${workspaceId}/courses/${course.id}/learn`)}
              >
                <Box
                  h='90px'
                  bg={course.cover_image ? undefined : 'bg.subtle'}
                  backgroundImage={course.cover_image ? `url(${course.cover_image})` : undefined}
                  backgroundSize='cover'
                  backgroundPosition='center'
                />
                <Box p={4}>
                  <Flex align='center' gap={2} mb={1.5}>
                    <Badge
                      bg={due?.urgent ? 'danger.subtle' : 'brand.soft'}
                      color={due?.urgent ? 'danger' : 'brand.fg'}
                      fontSize='2xs'
                      textTransform='uppercase'
                    >
                      {due?.urgent
                        ? 'Due soon'
                        : course.completed_item_count > 0
                          ? 'In progress'
                          : 'Assigned'}
                    </Badge>
                    {due && (
                      <Text fontSize='xs' color={due.urgent ? 'danger' : 'fg.muted'}>
                        {due.label}
                      </Text>
                    )}
                  </Flex>
                  <Text
                    fontFamily='heading'
                    fontSize='md'
                    fontWeight='800'
                    textTransform='uppercase'
                    letterSpacing='-0.01em'
                    color='fg.strong'
                    mb={1}
                  >
                    {course.name}
                  </Text>
                  {course.description && (
                    <Text fontSize='sm' color='fg.muted' lineClamp={2} mb={3}>
                      {course.description}
                    </Text>
                  )}
                  <ProgressBar
                    value={pct}
                    label={`${course.completed_item_count} of ${course.item_count} complete`}
                    valueLabel={`${Math.round(pct)}%`}
                  />
                </Box>
              </Box>
            );
          })}
        </Grid>
      )}
      {active.length === 0 && (
        <Text fontSize='sm' color='fg.muted' textAlign='center' mt={4} mb={2}>
          All caught up — everything assigned to you is complete.
        </Text>
      )}

      {completed.length > 0 && (
        <Box mt={7}>
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.2em'
            color='fg.muted'
            mb={2.5}
          >
            Completed
          </Text>
          <Box
            bg='bg.panel'
            borderWidth='1px'
            borderColor='border'
            borderRadius='lg'
            shadow='sm'
            overflow='hidden'
          >
            {completed.map((course) => (
              <Flex
                key={course.id}
                align='center'
                gap={3}
                px={4}
                py={3.5}
                borderBottomWidth='1px'
                borderColor='border'
                cursor='pointer'
                _hover={{ bg: 'bg.subtle' }}
                onClick={() => navigate(`/${workspaceId}/courses/${course.id}/learn`)}
              >
                <Icon as={CheckCircleIcon} w={5} h={5} color='success' />
                <Text fontSize='sm' fontWeight='600' color='fg.strong' flex='1' minW={0} truncate>
                  {course.name}
                </Text>
                <Text fontSize='sm' color='fg.muted' flexShrink={0}>
                  {course.completed_at
                    ? `Completed ${new Date(course.completed_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}`
                    : `${course.item_count} items complete`}
                </Text>
              </Flex>
            ))}
            <Box px={4} py={2.5}>
              <Text fontSize='xs' color='fg.muted'>
                Your completion record stays on your Coach Hub profile.
              </Text>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

// Hub table columns — shared by the header row and CourseRow so they can
// never drift apart. Completion gets its own column, matching the design.
const HUB_COLUMNS = '88px minmax(0,2fr) 0.7fr 0.85fr 0.8fr 1.05fr 24px';

function CourseRow({ course, workspaceId }: { course: Course; workspaceId: string }) {
  const navigate = useNavigate();
  const status = statusColors(course.status);
  const moduleLabel = `${course.module_count} ${course.module_count === 1 ? 'module' : 'modules'}`;
  // Authors land on the admin detail; everyone else goes straight into the
  // player. can_edit is server-computed and role-aware.
  const destination = course.can_edit
    ? `/${workspaceId}/courses/${course.id}`
    : `/${workspaceId}/courses/${course.id}/learn`;

  return (
    <Grid
      templateColumns={{ base: '1fr auto', md: HUB_COLUMNS }}
      gap={4}
      alignItems='center'
      px={2}
      py={4}
      borderBottomWidth='1px'
      borderColor='border'
      cursor='pointer'
      _hover={{ bg: 'bg.subtle' }}
      onClick={() => navigate(destination)}
    >
      <Box
        display={{ base: 'none', md: 'block' }}
        w='88px'
        h='58px'
        borderRadius='lg'
        bg={course.cover_image ? undefined : `slot${slotFor(course.name)}.bg`}
        backgroundImage={course.cover_image ? `url(${course.cover_image})` : undefined}
        backgroundSize='cover'
        backgroundPosition='center'
      />
      <Box minW={0}>
        <Text fontSize='sm' fontWeight='600' color='fg.strong' mb={0.5}>
          {course.name}
        </Text>
        {course.description && (
          <Text fontSize='sm' color='fg.muted' lineClamp={1} mb={1}>
            {course.description}
          </Text>
        )}
        <Flex align='center' gap={2}>
          <Text fontSize='xs' color='fg.subtle'>
            {moduleLabel}
            {course.item_count > 0 &&
              ` · ${course.item_count} ${course.item_count === 1 ? 'item' : 'items'}`}
          </Text>
          {course.created_by_name && (
            <Badge bg='bg.subtle' color='fg.muted' fontSize='2xs' fontWeight='500'>
              {course.created_by_name}
            </Badge>
          )}
        </Flex>
      </Box>
      <Box display={{ base: 'none', md: 'block' }}>
        <Badge bg={status.bg} color={status.color} textTransform='uppercase' fontSize='2xs'>
          {course.status}
        </Badge>
      </Box>
      <Text display={{ base: 'none', md: 'block' }} fontSize='sm' color='fg'>
        {course.audience_type || '—'}
      </Text>
      <Flex display={{ base: 'none', md: 'flex' }} align='center' gap={2}>
        <Icon
          as={course.access_mode === 'open' ? LinkIcon : LockClosedIcon}
          w={3.5}
          h={3.5}
          color='fg.muted'
        />
        <Text fontSize='sm' color='fg'>
          {course.access_mode === 'open' ? 'Open' : 'Log-in only'}
        </Text>
      </Flex>
      <Box display={{ base: 'none', md: 'block' }}>
        <CompletionCell course={course} />
      </Box>
      <Icon as={ChevronRightIcon} w={4} h={4} color='fg.subtle' justifySelf='end' />
    </Grid>
  );
}

// One Community Hub card: another org's shared course, previewable through
// its public link and copyable into this workspace as a draft.
function CommunityCourseCard({
  course,
  onAdd,
  isAdding,
}: {
  course: Course;
  onAdd: () => void;
  isAdding: boolean;
}) {
  return (
    <Box
      bg='bg.panel'
      borderWidth='1px'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      overflow='hidden'
      transition='box-shadow 0.15s'
      _hover={{ shadow: 'md' }}
    >
      <Box
        h='120px'
        bg={course.cover_image ? undefined : `slot${slotFor(course.name)}.bg`}
        backgroundImage={course.cover_image ? `url(${course.cover_image})` : undefined}
        backgroundSize='cover'
        backgroundPosition='center'
      />
      <Box p={4}>
        <Flex align='center' gap={2} mb={2}>
          <Avatar name={course.workspace_name} size='2xs' bg='bg.subtle' color='fg.muted' />
          <Text fontSize='xs' color='fg.muted' truncate>
            {course.workspace_name}
          </Text>
        </Flex>
        <Text
          fontFamily='heading'
          fontSize='md'
          fontWeight='800'
          textTransform='uppercase'
          letterSpacing='-0.01em'
          color='fg.strong'
          mb={1}
        >
          {course.name}
        </Text>
        {course.description && (
          <Text fontSize='sm' color='fg.muted' lineClamp={2} mb={3}>
            {course.description}
          </Text>
        )}
        <Flex align='center' gap={2.5} mb={4}>
          {course.audience_type && (
            <Badge
              bg={`slot${slotFor(course.audience_type)}.bg`}
              color={`slot${slotFor(course.audience_type)}.fg`}
              textTransform='uppercase'
              fontSize='2xs'
            >
              {course.audience_type}
            </Badge>
          )}
          <Text fontSize='sm' color='fg.muted'>
            {course.module_count} {course.module_count === 1 ? 'module' : 'modules'}
          </Text>
        </Flex>
        <Flex gap={2}>
          <Button variant='outline' size='sm' asChild>
            <a
              href={buildPublicUrl('courses', course.reference_id)}
              target='_blank'
              rel='noreferrer'
            >
              Preview
            </a>
          </Button>
          <Button variant='primary' size='sm' loading={isAdding} onClick={onAdd}>
            Add to workspace
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}

// Courses hub, shaped per role like the prototype: admins get
// {Workspace} Hub / Community Hub / My Hub (Assigned appears only if they
// actually have assignments), contributors get My Hub (default) /
// Community Hub / Assigned to me with no org-wide tab, and viewers get no
// tabs at all — their page IS the assigned view, titled "My Courses".
export default function ListCourses() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const isViewer = useIsViewer();
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const { user } = useCurrentUser();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { items, isLoading, error, loadCourses, loadedWorkspaceId } = useCoursesStore();

  // isViewer stays null until the auth role resolves; contributor means the
  // role is known and is neither admin nor viewer.
  const isContributor = isViewer != null && !isViewer && !isAdmin;

  const [tab, setTab] = useState<HubTab>('workspace');
  const [tabTouched, setTabTouched] = useState(false);

  // Contributors default to their own hub — corrected here rather than in the
  // useState initializer because the role can resolve after first render.
  useEffect(() => {
    if (!tabTouched && isContributor) setTab('mine');
  }, [isContributor, tabTouched]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL);
  const [audienceFilter, setAudienceFilter] = useState(ALL);
  const [accessFilter, setAccessFilter] = useState(ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [assigned, setAssigned] = useState<Course[]>([]);

  // Community Hub state, loaded lazily the first time the tab opens.
  const [community, setCommunity] = useState<Course[]>([]);
  const [communityLoaded, setCommunityLoaded] = useState(false);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [sportFilter, setSportFilter] = useState(ALL);
  const [orgFilter, setOrgFilter] = useState(ALL);
  const [addingId, setAddingId] = useState<number | null>(null);

  useEffect(() => {
    if (workspaceId) loadCourses(workspaceId);
  }, [workspaceId, loadCourses]);

  // "Assigned to me" loads its own list — the courses reaching the caller
  // directly or through group membership, with their progress counts.
  useEffect(() => {
    if (!workspaceId) return;
    api
      .get(`courses/assigned/workspace/${workspaceId}`)
      .then((res) => setAssigned(unwrap<Course[]>(res) ?? []))
      .catch(() => setAssigned([]));
  }, [workspaceId]);

  useEffect(() => {
    if (tab !== 'community' || communityLoaded || !workspaceId) return;
    setCommunityLoading(true);
    api
      .get(`courses/community/workspace/${workspaceId}`)
      .then((res) => {
        setCommunity(unwrap<Course[]>(res) ?? []);
        setCommunityLoaded(true);
      })
      .catch(() => {
        toaster.create({ title: 'Failed to load community courses', type: 'error' });
      })
      .finally(() => setCommunityLoading(false));
  }, [tab, communityLoaded, workspaceId]);

  const visible = useMemo(() => {
    // Guard against a stale list flashing through right after a workspace switch.
    if (loadedWorkspaceId !== workspaceId) return [];
    let courses = items;
    if (!isViewer && tab === 'mine') {
      courses = courses.filter((c) => c.created_by === user?.id);
    }
    if (statusFilter !== ALL) {
      courses = courses.filter((c) => c.status === statusFilter);
    }
    if (audienceFilter !== ALL) {
      courses = courses.filter((c) => c.audience_type === audienceFilter);
    }
    if (accessFilter !== ALL) {
      courses = courses.filter((c) => c.access_mode === accessFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      courses = courses.filter(
        (c) =>
          c.name.toLowerCase().includes(query) || c.description.toLowerCase().includes(query),
      );
    }
    return courses;
  }, [
    items,
    loadedWorkspaceId,
    workspaceId,
    isViewer,
    tab,
    statusFilter,
    audienceFilter,
    accessFilter,
    searchQuery,
    user?.id,
  ]);

  const communityVisible = useMemo(() => {
    let courses = community;
    if (sportFilter !== ALL) {
      courses = courses.filter((c) => c.sport_type_name === sportFilter);
    }
    if (audienceFilter !== ALL) {
      courses = courses.filter((c) => c.audience_type === audienceFilter);
    }
    if (orgFilter !== ALL) {
      courses = courses.filter((c) => c.workspace_name === orgFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      courses = courses.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          (c.workspace_name ?? '').toLowerCase().includes(query),
      );
    }
    return courses;
  }, [community, sportFilter, audienceFilter, orgFilter, searchQuery]);

  const audienceItems = useMemo(
    () =>
      optionsFrom(
        tab === 'community' ? community : items,
        (c) => c.audience_type,
        'All audiences',
      ),
    [tab, community, items],
  );
  const sportItems = useMemo(
    () => optionsFrom(community, (c) => c.sport_type_name, 'All sports'),
    [community],
  );
  const orgItems = useMemo(
    () => optionsFrom(community, (c) => c.workspace_name, 'All organizations'),
    [community],
  );

  const handleAddToWorkspace = async (course: Course) => {
    setAddingId(course.id);
    try {
      const res = await api.post(`courses/${course.id}/copy`, {
        workspace_id: Number(workspaceId),
      });
      const copied = unwrap<Course>(res);
      toaster.create({ title: `Added "${course.name}" to your hub as a draft`, type: 'success' });
      loadCourses(workspaceId);
      navigate(`/${workspaceId}/courses/${copied.id}`);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to add course',
        type: 'error',
      });
    } finally {
      setAddingId(null);
    }
  };

  const subtitles: Record<HubTab, string> = {
    workspace: 'Everything your organization has published. Build, share, and track completion.',
    community:
      'Courses other organizations have shared with the Coach Hub community. Add one to your hub and assign it to your own people.',
    mine: 'Courses you have created. They stay in your hub until you publish them to the organization.',
    assigned: 'Courses assigned to you, with your progress and season pace.',
  };

  const isHubList = tab === 'workspace' || tab === 'mine';
  const tabSelected = { color: 'brand.fg', fontWeight: '700' };

  // Viewers: no tabs, no filters — the page is their personal course list.
  if (isViewer) {
    return (
      <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
        <Container>
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.2em'
            color='fg.muted'
            mb={1}
          >
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}
          </Text>
          <Heading as='h1' size='3xl' mb={6}>
            My Courses
          </Heading>
          {assigned.length === 0 ? (
            <Text fontSize='sm' color='fg.muted' textAlign='center' mt={8}>
              Nothing has been assigned to you yet.
            </Text>
          ) : (
            <AssignedTab assigned={assigned} workspaceId={workspaceId} />
          )}
        </Container>
      </Box>
    );
  }

  // Admins only see the Assigned tab when something actually reaches them;
  // contributors always have it (it's a first-class part of their hub).
  const tabOrder: HubTab[] = isContributor
    ? ['mine', 'community', 'assigned']
    : assigned.length > 0
      ? ['workspace', 'community', 'mine', 'assigned']
      : ['workspace', 'community', 'mine'];

  const tabLabels: Record<HubTab, string> = {
    workspace: workspace?.name ? `${workspace.name} Hub` : 'Workspace Hub',
    community: 'Community Hub',
    mine: 'My Hub',
    assigned: `Assigned to me${assigned.length > 0 ? ` (${assigned.length})` : ''}`,
  };

  return (
    <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Container>
        <Flex mb={2} alignItems='center' gap={4} direction={{ base: 'column', md: 'row' }}>
          <Heading as='h1' size='3xl'>
            Courses
          </Heading>
          <Spacer />
          {!isCancelled && (
            <Button variant='primary' asChild>
              <NavLink to={`/${workspaceId}/courses/create`}>
                <Icon as={PlusIcon} w={4} h={4} />
                New course
              </NavLink>
            </Button>
          )}
        </Flex>

        <Text fontSize='sm' color='fg.muted' mb={6} maxW='2xl'>
          {subtitles[tab]}
        </Text>

        {/* The prototype wraps the tabs and hub content in one white panel
            on the page background — the tab row sits inside the card. */}
        <Box
          bg='bg.panel'
          borderWidth='1px'
          borderColor='border'
          borderRadius='xl'
          shadow='sm'
          overflow='hidden'
        >
          <Tabs.Root
            value={tab}
            onValueChange={(e) => {
              setTab(e.value as HubTab);
              setTabTouched(true);
            }}
            variant='line'
            size='md'
          >
            <Tabs.List px={5} pt={2}>
              {tabOrder.map((value) => (
                <Tabs.Trigger
                  key={value}
                  value={value}
                  fontWeight='600'
                  color='fg'
                  _selected={tabSelected}
                >
                  {tabLabels[value]}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>

          <Box px={5} pt={5} pb={6}>

        {tab === 'assigned' && (
          <>
            {assigned.length === 0 ? (
              <Text fontSize='sm' color='fg.muted' textAlign='center' mt={8}>
                Nothing has been assigned to you yet.
              </Text>
            ) : (
              <AssignedTab assigned={assigned} workspaceId={workspaceId} />
            )}
          </>
        )}

        {tab !== 'assigned' && (
          <>
            <Box mb={3}>
              <Search
                searchQuery={searchQuery}
                onSearchChange={(e) => setSearchQuery(e.target.value)}
                onClearSearch={() => setSearchQuery('')}
                placeholder={
                  tab === 'community' ? 'Search community courses …' : 'Search courses …'
                }
              />
            </Box>

            <Flex gap={2.5} mb={4} align='center' wrap='wrap'>
              {isHubList ? (
                <>
                  <SimpleSelect
                    items={STATUS_ITEMS}
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                    size='sm'
                    width='150px'
                  />
                  <SimpleSelect
                    items={audienceItems}
                    value={audienceFilter}
                    onValueChange={setAudienceFilter}
                    size='sm'
                    width='170px'
                  />
                  <SimpleSelect
                    items={ACCESS_ITEMS}
                    value={accessFilter}
                    onValueChange={setAccessFilter}
                    size='sm'
                    width='150px'
                  />
                </>
              ) : (
                <>
                  <SimpleSelect
                    items={sportItems}
                    value={sportFilter}
                    onValueChange={setSportFilter}
                    size='sm'
                    width='150px'
                  />
                  <SimpleSelect
                    items={audienceItems}
                    value={audienceFilter}
                    onValueChange={setAudienceFilter}
                    size='sm'
                    width='170px'
                  />
                  <SimpleSelect
                    items={orgItems}
                    value={orgFilter}
                    onValueChange={setOrgFilter}
                    size='sm'
                    width='180px'
                  />
                  <Text fontSize='sm' color='fg.subtle' display={{ base: 'none', md: 'block' }}>
                    Filter by what the course covers
                  </Text>
                </>
              )}
            </Flex>
          </>
        )}

        {isHubList && (
          <>
            <Flex
              align='center'
              justify='space-between'
              py={3}
              borderBottomWidth='1px'
              borderColor='border'
            >
              <Text fontSize='sm' color='fg.muted'>
                {visible.length} of {items.length} courses
              </Text>
              <Text fontSize='sm' color='fg.muted'>
                Recently updated
              </Text>
            </Flex>

            {/* Column headers — display tier, uppercase, tracked */}
            <Grid
              display={{ base: 'none', md: 'grid' }}
              templateColumns={HUB_COLUMNS}
              gap={4}
              px={2}
              pt={4}
              pb={2}
            >
              <span />
              {['Course', 'Status', 'Audience', 'Access', 'Completion'].map((label) => (
                <Text
                  key={label}
                  fontFamily='heading'
                  fontSize='2xs'
                  fontWeight='600'
                  textTransform='uppercase'
                  letterSpacing='0.06em'
                  color='fg.muted'
                >
                  {label}
                </Text>
              ))}
              <span />
            </Grid>

            {visible.map((course) => (
              <CourseRow key={course.id} course={course} workspaceId={workspaceId} />
            ))}

            {isLoading && (
              <Center mt={6}>
                <Spinner size='lg' />
              </Center>
            )}
            {!isLoading && error && (
              <Text fontSize='sm' color='danger' textAlign='center' mt={6}>
                {error}
              </Text>
            )}
            {!isLoading && !error && visible.length === 0 && (
              <Text fontSize='sm' color='fg.muted' textAlign='center' mt={6}>
                {items.length === 0
                  ? 'No courses yet — build your first one.'
                  : 'No courses match the current filters.'}
              </Text>
            )}
          </>
        )}

        {tab === 'community' && (
          <>
            <Flex
              align='center'
              justify='space-between'
              py={3}
              mb={4}
              borderBottomWidth='1px'
              borderColor='border'
            >
              <Text fontSize='sm' color='fg.muted'>
                {communityVisible.length} of {community.length} courses
              </Text>
              <Text fontSize='sm' color='fg.muted'>
                Recently updated
              </Text>
            </Flex>

            {communityLoading ? (
              <Center mt={6}>
                <Spinner size='lg' />
              </Center>
            ) : communityVisible.length === 0 ? (
              <Text fontSize='sm' color='fg.muted' textAlign='center' mt={6}>
                {community.length === 0
                  ? 'No community courses yet — organizations that share theirs will show up here.'
                  : 'No community courses match the current filters.'}
              </Text>
            ) : (
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
                {communityVisible.map((course) => (
                  <CommunityCourseCard
                    key={course.id}
                    course={course}
                    isAdding={addingId === course.id}
                    onAdd={() => handleAddToWorkspace(course)}
                  />
                ))}
              </Grid>
            )}
          </>
        )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
